/**
 * Write-only-differences helpers for hierarchy-resolved boolean config fields
 * (ADR 0032). A toggle seeded from the parent must NOT be persisted verbatim:
 * `getConfig` is most-specific-wins, so stamping the inherited value onto the
 * child freezes that level and a later parent change never reaches it again.
 *
 * The server `$set`s the whole `config` subdoc and `undefined` is dropped by
 * JSON, so returning `undefined` (= omit the key) is exactly "inherit". This
 * mirrors the inline logic in SettingsForm.tsx's onSubmit — one contract, two
 * call sites (Settings drawer, Tiebreakers modal).
 *
 * `fallback` is "what this entity would inherit when NO ancestor sets the
 * field" — i.e. the platform default. The `false` default parameter happens to
 * match `CONFIG_DEFAULTS` (ADR 0035) for the hide-flags/toggles this helper
 * serves today (hideTeams, hideSchedules, hideStandings, showAllTeams,
 * disableSync, breakPlayoffTiesBySeed — all false). Where the field is known,
 * callers should pass `CONFIG_DEFAULTS.<field>` explicitly rather than relying
 * on this parameter default. NOTE: for true-default fields (requirePlayers,
 * requireCoaches) passing the table value would change WHAT GETS PERSISTED
 * (an explicit `true` would become "omit/inherit") — do not flip an existing
 * call site's fallback without deciding that write-semantics change on purpose.
 */

export function inheritedBool(
  parentConfig: Record<string, unknown> | null | undefined,
  field: string,
  fallback = false
): boolean {
  const parent = parentConfig ? parentConfig[field] : undefined;
  return typeof parent === 'boolean' ? parent : fallback;
}

/**
 * The value to persist for `field`: the boolean itself when it differs from
 * what this entity would inherit, `undefined` (omit the key → inherit) when
 * it matches. Note the match case also CLEANS an existing stamped override —
 * setting a toggle back to the inherited value removes the child's copy.
 */
export function boolOrInherit(
  parentConfig: Record<string, unknown> | null | undefined,
  field: string,
  value: boolean,
  fallback = false
): boolean | undefined {
  return !!value === inheritedBool(parentConfig, field, fallback) ? undefined : !!value;
}

interface TiebreakerEntry {
  id: string;
  maxPointsScored?: number | null;
  maxPointDifferential?: number | null;
  hidden?: boolean;
}

// Normalize a chain for comparison: fixed key order + canonical empties, so
// `hidden` absent vs false and `null` vs undefined caps compare equal.
const normalizeChain = (chain: TiebreakerEntry[] | undefined) =>
  (chain || []).map((t) => ({
    id: t.id,
    maxPointsScored: t.maxPointsScored ?? null,
    maxPointDifferential: t.maxPointDifferential ?? null,
    hidden: !!t.hidden,
  }));

/**
 * Array flavor of boolOrInherit for `config.tiebreakers[sport]` (ADR 0032).
 * Returns the chain to persist, or `undefined` (omit the key → inherit) when:
 *   - the chain is EMPTY — getConfig treats an empty array as "not set"
 *     (meaningful-guard), so persisting it is dead weight AND would suggest an
 *     opt-out that cannot exist (same trade-off as eligibility: removing all
 *     rules reverts to the inherited order), or
 *   - the chain deep-equals what this entity would inherit — the modal seeds
 *     from the effective chain, so saving untouched must not stamp it.
 */
export function chainOrInherit(
  parentConfig: Record<string, unknown> | null | undefined,
  sportId: string,
  chain: TiebreakerEntry[]
): TiebreakerEntry[] | undefined {
  const own = normalizeChain(chain);
  if (own.length === 0) return undefined;
  const parentChain = (parentConfig as any)?.tiebreakers?.[sportId] as TiebreakerEntry[] | undefined;
  const inherited = normalizeChain(parentChain);
  return JSON.stringify(own) === JSON.stringify(inherited) ? undefined : chain;
}
